<template>
  <div>
    <b-row>
      <b-col cols="9">
        <PageIssues
          v-if="
            (page.status_code && page.status_code != 200) || perfScore === 0
          "
          :page="page"
          :page_issues="issues.page_issues"
        />
        <template v-else>
          <div
            v-if="(page.status || page.status_inferred) == 'monitoring' || page.improved_at"
            :style="{ width: '810px' }"
          >
            <PagesTraffic
              type="completed"
              :site="site"
              :page="page"
              :allow-forecast="false"
              :details="false"
              :height="280"
            />
            <hr />
          </div>
          <b-row class="my-4">
            <b-col cols="1" class="col-score">
              <small>Title Score</small>
              <TitleScore :score="title.score" :width="70" :height="70" />
            </b-col>
            <b-col>
              <div class="mb-2">
                <p class="lead">
                  <strong>Step 1:</strong> Optimize your title tag to improve
                  rankings and click-through rates:
                </p>
              </div>
              <div class="mb-4">
                <TitleEditor
                  v-if="showTitle"
                  ref="TitleEditor"
                  :account="account"
                  :page="page"
                  :site="site"
                  :isDuda="isDuda"
                  @update="onTitleUpdate"
                  @approve="approve()"
                  @revert="revert()"
                />
              </div>
              <TitleReasons
                v-if="showTitle"
                :site="site"
                :page="page"
                :title="title"
                @update="triggerTitleUpdate"
                :show_all="false"
                @ignore="toggleIgnoreIssue"
              />
            </b-col>
          </b-row>
          <hr />
          <b-row class="my-4">
            <b-col cols="1" class="col-score">
              <small>Link Score</small>
              <TitleScore :score="linkScore" :width="70" :height="70" />
            </b-col>
            <b-col>
              <p class="lead">
                <strong>Step 2:</strong> Improve internal linking and anchor
                text to boost authority:
              </p>
              <LinkIssues
                :account="account"
                :link_issues="issues.link_issues"
                :loading="$apollo.queries.issues.loading"
                :link_opportunities="link_opportunities"
                :page="page"
                :ignore_nav.sync="ignore_nav"
                :filter.sync="filter"
                :isDuda="isDuda"
                @ignore="toggleIgnoreIssue"
              />
            </b-col>
          </b-row>
          <hr />
        </template>
        <template
          v-if="
            (perfScore !== 0 && perfScore < 100) ||
            (perfScore == 100 && page.perf_improved_at && page.perf_improved_at != page.updated_at)
          "
        >
          <b-row class="my-4">
            <b-col cols="1" class="col-score">
              <small>Perf Score</small>
              <TitleScore :score="perfScore" :width="70" :height="70" />
            </b-col>
            <b-col>
              <p class="lead" v-if="perfScore > 0">
                <strong>Step 3:</strong> Improve page speed by fixing
                performance issues:
              </p>

              <PageIssues
                v-if="issues.page_issues.length"
                :page="page"
                :page_issues="issues.page_issues"
              />
              <AssetIssues
                v-if="issues.asset_issues.length"
                :page="page"
                :asset_issues="issues.asset_issues"
                @ignore="toggleIgnoreIssue"
              />
              <div class="text-small mt-2">
                <a
                  :href="`https://pagespeed.web.dev/report?url=${encodeURIComponent(
                    page.url
                  )}`"
                  target="_blank"
                  >Google PageSpeed Report
                  <b-icon-box-arrow-up-right class="ml-1"
                /></a>
              </div>
            </b-col>
          </b-row>
          <hr />
        </template>

        <b-row class="mt-4">
          <b-col cols="1" class="col-score"> </b-col>
          <b-col>
            <p class="lead mb-0"><strong>Page Activity</strong></p>
          </b-col>
        </b-row>
        <div class="mb-4">
          <PageHistory :page="page" />
        </div>
      </b-col>
      <b-col cols="3" class="bg-light">
        <table v-if="pageStatus" class="text-small my-4">
          <tr v-if="!isDuda">
            <td class="pb-3 pr-2">Optimization status:</td>
            <td class="pb-3">
              <b-dropdown :text="pageStatus" variant="primary" size="sm">
                <b-dropdown-item-button
                  v-for="[key, name] of Object.entries(statuses)"
                  :key="key"
                  @click="$emit('status', key)"
                  :active="pageStatus == name"
                  class="text-small"
                >
                  {{ name }}
                </b-dropdown-item-button>
              </b-dropdown>
            </td>
          </tr>
          <tr>
            <td class="pr-2">
              <div v-if="page.crawled_at" :title="crawledAt">
                Crawled {{ page.crawled_at | relativeTime }}.
              </div>
              <div v-else>Not yet crawled.</div>
            </td>
            <td>
              <div v-if="crawling" class="text-muted">
                Crawling <b-spinner v-if="crawling" small />
              </div>
              <div v-else-if="!isDuda">
                <b-link @click="$emit('crawl')">Re-crawl</b-link>
              </div>
            </td>
          </tr>
        </table>
        <div class="pt-4 my-4">
          <PageTraffic
            v-if="page.clicks"
            :page_id="page.id"
            :status="page.status || this.page.status_inferred"
            :potential="page.potential"
            :site_clicks_delta="
              page.site_page_clicks_all ? page.site_page_clicks_all.delta : null
            "
            :delta_month="
              page.clicks_last_month ? page.clicks_last_month.delta * 100 : null
            "
            :this_month="page.clicks"
            :last_month="
              page.clicks_last_month ? page.clicks_last_month.clicks : null
            "
            :improved_at="page.improved_at"
          />
        </div>
        <div class="my-4">
          <PageWords
            v-if="queryWords.length"
            :page="page"
            :query_words="queryWords"
            @refresh="refresh(true)"
          />
        </div>
        <div class="my-4 py-4">
          <PageSerp
            v-if="page.id == $route.params.page_id && (page.serp_result || page.language)"
            :page="page"
          />
        </div>
        <AnchorTexts v-if="page.inbound_links.length" :links="page.inbound_links" />
        <div v-if="isDuda" class="pt-4 my-4">
          <small class="d-block mb-3">Frequently Asked Questions</small>
          <div v-for="item, i of faqs" :key="i" class="word-list-item mb-1 text-small text-muted">
            <b-link v-b-toggle="`faq-${i}`">
              {{item.q}}
            </b-link>
            <b-collapse :id="`faq-${i}`" accordion="faqs">
              {{item.a}}
            </b-collapse>
          </div>
        </div>
      </b-col>
    </b-row>
  </div>
</template>
<style lang="scss">
.col-1.col-score {
  text-align: center;
  flex: 0 0 10%;
  max-width: 10%;
  margin-top: 4px;
  padding-right: 0;
}
hr {
  margin: 2rem 0;
}
</style>
<script>
import _ from 'lodash'
import PageTraffic from '@/components/PageTraffic'
import PagesTraffic from '@/components/PagesTraffic'
import TitleEditor from '@/components/TitleEditor'
import PageIssues from '@/components/PageIssues'
import AssetIssues from '@/components/AssetIssues'
import TitleReasons from '@/components/TitleReasons'
import LinkIssues from '@/components/LinkIssues'
import TitleScore from '@/components/TitleScore'
import PageSerp from '@/components/PageSerp'
import PageWords from '@/components/PageWords'
import KeywordInput from '@/components/KeywordInput'
import AnchorTexts from '@/components/AnchorTexts'
import Approve from '@/components/Approve'
import PageHistory from '@/components/PageHistory'
import PAGE_MUTATE from '@/graphql/PageMutate.gql'
import WORDS_QUERY from '@/graphql/WordsQuery.gql'
import ISSUES_QUERY from '@/graphql/IssuesQuery.gql'
import LINK_OPPORTUNITIES_QUERY from '@/graphql/LinkOpportunitiesQuery.gql'
import PAGE_ISSUE_MUTATION from '@/graphql/PageIssueMutation.gql'

export default {
  name: 'PageSimple',
  components: {
    PageSerp,
    PageWords,
    PageTraffic,
    PagesTraffic,
    TitleEditor,
    PageIssues,
    AssetIssues,
    TitleReasons,
    LinkIssues,
    TitleScore,
    PageWords,
    KeywordInput,
    AnchorTexts,
    Approve,
    PageHistory
  },
  props: ['account', 'site', 'page', 'site_delta', 'crawling', 'isDuda'],

  data: () => {
    return {
      title: {},
      edited: false,
      loaded: false,
      issues: { link_issues: [], page_issues: [], asset_issues: [] },
      link_opportunities: [],
      wordTags: [],
      words: [],
      ignore_nav: false,
      statuses: {
        ignored: 'Ignored',
        not_started: 'Not Started',
        in_progress: 'In Progress',
        completed: 'Monitoring',
        archived: 'Archived'
      },
      filter: '',
      faqs: [
        {
          q: 'How do I update my title tag?',
          a: 'From the sidebar, click Pages › ⚙ › SEO › Page Title'
        },
        {
          q: 'Why are some words underlined in green?',
          a: 'These are top keywords that are are present in your title.'
        },
        {
          q: 'Why are there no Link Opportunities?',
          a: `This is often the case for pages that are linked to within the site's global navigation.`
        },
        {
          q: 'How do I reduce the size of my CSS or JS?',
          a: `Widgets, page layouts, and apps can all add CSS and JS to your page.`
        },
      ]
    }
  },

  apollo: {
    words: {
      query: WORDS_QUERY,
      variables() {
        return {
          queries: this.page.queries.map((e) => _.omit(e, '__typename')),
          limit: 7,
          score: 0
        }
      },
      skip() {
        return !this.page.id || _.isEmpty(this.page.queries)
      },
      result(res) {
        if (!res.stale && this.words_page_id != this.page.id) {
          // prevents title_opportunities from loading with the wrong words
          this.words_page_id = this.page.id
        }
      }
    },
    issues: {
      query: ISSUES_QUERY,
      variables() {
        return {
          page_id: this.page.id
        }
      },
      skip() {
        return !this.page.id
      }
    },
    link_opportunities: {
      query: LINK_OPPORTUNITIES_QUERY,
      variables() {
        return {
          page_id: this.page.id,
          ignore_nav: this.ignore_nav,
          words: this.queryWords
            .filter(
              (w) =>
                w.score >= process.env.VUE_APP_WORD_CLICK_THRESHOLD &&
                !w.ignored
            )
            .map((e) => e.word),
          filter: this.filter
        }
      },
      skip() {
        return (
          !this.page.id ||
          _.isEmpty(this.words) ||
          this.$apollo.queries.words.loading ||
          this.words_page_id != this.page.id
        )
      },
      debounce: 500
    }
  },

  watch: {
    words: {
      deep: true,
      handler() {
        this.wordTags = _.union(
          this.wordTags,
          this.words
            .map((e) => e.word)
            .filter(
              (w) =>
                !this.page.query_words.some((i) => i.ignored && i.word == w)
            )
        )
      }
    },
    wordTags(to, from) {
      if (to.length + 1 == from.length) {
        const removed = from.find((e) => !to.includes(e))
        this.toggleIgnore({ word: removed, ignored: false })
      }
    }
  },

  computed: {
    linkScore() {
      return this.issues.link_score || this.page.link_score
    },
    perfScore() {
      return this.issues.perf_score || this.page.perf_score
    },
    queryWords() {
      if (!this.page.id) return []
      const queryWords = this.words || []
      return queryWords
        .map((w) => ({
          ...w,
          ignored: this.page.query_words.some(
            (e) => e.ignored && e.word === w.word
          )
        }))
        .concat(
          this.page.query_words.filter(
            (w) => !queryWords.some((e) => e.word === w.word)
          )
        )
        .map((e) => (!e.hasOwnProperty('score') ? { ...e, score: 10 } : e))
    },
    ignoredWords() {
      return (this.queryWords || []).filter((e) => e.ignored)
    },
    isLowData() {
      return this.page.queries && _.sumBy(this.page.queries, 'clicks') < 10
    },
    annotations() {
      if (!this.page.approved_at) {
        return []
      }
      return [
        {
          date: new Date(this.page.approved_at),
          text: 'Approved'
        }
      ]
    },
    showTitle() {
      return !!this.page.title
    },
    showScore() {
      return typeof this.title.score == 'number'
    },
    showTitlePopover() {
      return (
        !_.get(this, 'site.total_drafts.aggregate.count', true) &&
        !_.get(this, 'site.total_approved.aggregate.count', true) &&
        this.page.title_score != 100 &&
        this.showTitle &&
        !this.page.is_draft
      )
    },
    pageStatus() {
      return this.statuses[this.page.status || this.page.status_inferred]
    },
    crawledAt() {
      return (
        this.$options.filters.formatDate(this.page.crawled_at) +
        ' ' +
        this.$options.filters.formatTime(this.page.crawled_at)
      )
    }
  },
  methods: {
    async refresh() {
      this.$emit('refresh')
      await this.$apollo.queries.words.refetch()
      await this.$apollo.queries.issues.refetch()
      await this.$apollo.queries.link_opportunities.refetch()
    },
    // Emitted by TitleEditor when the title is changed/saved
    async onTitleUpdate(val) {
      this.title = val
      await this.$nextTick()
      if (this.title.score != this.page.title_score) {
        this.$emit('refresh')
      }
      this.$emit('titleOffset', this.$refs.TitleEditor.$el.offsetTop)
    },
    // Used to call Update within TitleEditor
    triggerTitleUpdate(val) {
      this.$refs.TitleEditor.save(val)
    },
    focus() {
      const $vm = this
      setTimeout(
        function () {
          $vm.$refs.TitleEditor.focus()
        },
        $vm.$refs.TitleEditor.focus ? 0 : 100
      )
    },
    async approve() {
      const isApproved = true
      _zyppy.track('Page Approved', {
        isApproved,
        ..._.pick(this.page, ['id', 'custom_title', 'title'])
      })
      const changes = {
        optimized_title: isApproved ? this.page.custom_title : null,
        approved_at: isApproved && this.page.custom_title ? new Date() : null,
        published_at: null
      }
      await this.$apollo.mutate({
        mutation: PAGE_MUTATE,
        variables: {
          id: this.page.id,
          changes
        }
      })
    },
    revert() {
      _zyppy.track('Revert Clicked')
      this.triggerTitleUpdate()
    },
    async toggleIgnoreIssue(name, ignored) {
      await this.$apollo.mutate({
        mutation: PAGE_ISSUE_MUTATION,
        variables: {
          name,
          page_id: this.page.id,
          site_id: this.page.site_id,
          ignored: !ignored
        }
      })
      if (!this.page.status && this.page.status_inferred == 'not_started') {
        await this.$apollo.mutate({
          mutation: PAGE_MUTATE,
          variables: {
            id: this.page.id,
            changes: {
              status: 'in_progress'
            }
          }
        })
      }
      await this.$nextTick()
      this.$emit('refresh')
      await this.$apollo.queries.issues.refetch()
    }
  }
}
</script>